// src/pages/Auth/Start.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CompassLogo from "components/icons/CompassLogo";
import { supabase } from "services/supabaseClient";
import SectionCard from "components/SectionCard";
import FooterCompact from "components/FooterCompact";
import { logAudit } from "lib/audit";
import { isProfileComplete } from "lib/profileCompletion";
import { useDialog } from "context/DialogContext";
import {
  clearActivityState,
  clearSessionLogoutFlags,
  isIdleSessionExpired,
  writeLastActivity,
} from "lib/sessionActivity";

/* ────────────────────────────────
   Shared loading spinner
──────────────────────────────── */
function LoadingUserScreen({ label = "Loading..." }) {
  return (
    <div className="flex items-center justify-center h-screen bg-app text-app">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-current mx-auto mb-4"></div>
        <p>{label}</p>
      </div>
    </div>
  );
}

function isLockedAccountError(err) {
  const code = String(err?.code || "").toLowerCase();
  const message = String(err?.message || "").toLowerCase();

  return (
    code === "user_banned" ||
    message.includes("user is banned") ||
    message.includes("account locked")
  );
}

/* ────────────────────────────────
   Main component
──────────────────────────────── */
export default function Start() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState(null);

  // loading = network work for login/mfa actions
  const [loading, setLoading] = useState(false);

  // routing = we are deciding where to send the user after auth
  const [routing, setRouting] = useState(false);

  const [show2FA, setShow2FA] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [challengeId, setChallengeId] = useState(null);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const submitBtnRef = useRef(null);

  const { alert, confirm } = useDialog();

  const flashSubmit = () => {
    const btn = submitBtnRef.current;
    if (!btn) return;
    btn.classList.add("kbd-press");
    setTimeout(() => btn.classList.remove("kbd-press"), 150);
  };

  const handleFormKeyDown = (e) => {
    if (e.key === "Enter") flashSubmit();
  };

  useEffect(() => {
    if (pathname !== "/" && pathname !== "/start") return;

    try {
      if (localStorage.getItem("compass.disabledAccount") === "1") {
        localStorage.removeItem("compass.disabledAccount");
        setError(
          "This account has been suspended. Please contact your administrator."
        );
      }
    } catch {
      // ignore storage failures
    }
  }, [pathname]);

  /**
   * Route the user after auth without flashing the Start page.
   * - Keep routing=true until navigation decision is made.
   * - Only force onboarding if profile row is visible and incomplete.
   */
  const routeAfterAuth = useCallback(async () => {
    setRouting(true);
    setError("");

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setRouting(false);
        return;
      }

      writeLastActivity(Date.now());

      const { data: p, error: pErr } = await supabase
        .from("profiles")
        .select("first_name,last_name,tenant_id,is_financial_pro,is_admin,disabled")
        .eq("id", user.id)
        .maybeSingle();

      // If we cannot read the profile (error OR null), do NOT force signup-user.
      // Let them proceed to dashboard.
      if (pErr || !p) {
        navigate("/dashboard", { replace: true });
        return;
      }

      if (p.disabled) {
        try {
          localStorage.setItem("compass.disabledAccount", "1");
          clearActivityState();
          await supabase.auth.signOut();
        } catch {
          // ignore sign-out/storage failures
        }

        setError("This account has been suspended. Please contact your administrator.");
        navigate("/", { replace: true });
        return;
      }

      if (!isProfileComplete(p)) {
        navigate("/signup-user", { replace: true });
        return;
      }

      navigate("/dashboard", { replace: true });

      // restore session prompt (optional)
      setTimeout(() => {
        void (async () => {
          const hasDraft = sessionStorage.getItem("debtstudio.session");
          const wasInactive = localStorage.getItem("compass.inactivityLogout");

          if (hasDraft && wasInactive) {
            localStorage.removeItem("compass.inactivityLogout");

            const restore = await confirm(
              "Auto-logoff detected. Restore your last session?",
              {
                title: "Restore session",
                okLabel: "Restore",
                cancelLabel: "Discard",
              }
            );

            if (restore) {
              navigate("/debt-studio");
            } else {
              sessionStorage.removeItem("debtstudio.session");
            }
          }
        })();
      }, 300);
    } finally {
      // We intentionally do NOT set routing=false here because navigation will unmount Start.
      // If navigation fails for some reason, we allow the UI back.
      setTimeout(() => {
        setRouting(false);
      }, 500);
    }
  }, [confirm, navigate]);

  /* ─────────────── Lifecycle ─────────────── */
  useEffect(() => {
    if (pathname !== "/" && pathname !== "/start") return;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        navigate("/reset-password", { replace: true });
      }
    });

    // If the URL indicates a recovery flow, route immediately
    try {
      const url = new URL(window.location.href);
      const qType = url.searchParams.get("type");
      const hasHashRecovery = url.hash.includes("type=recovery");
      const hasCode = url.searchParams.get("code");
      const hasAccessToken = url.hash.includes("access_token=");

      if (qType === "recovery" || hasHashRecovery || hasCode || hasAccessToken) {
        navigate("/reset-password", { replace: true });
      }
    } catch {
      // ignore
    }

    // If already signed in, do not show Start, route immediately
    void (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        if (isIdleSessionExpired()) {
          try {
            localStorage.setItem("compass.inactivityLogout", "1");
            localStorage.removeItem("compass.manualLogout");
            clearActivityState();
          } catch {
            // ignore storage failures
          }

          await supabase.auth.signOut();
          navigate("/session-expired", { replace: true });
          return;
        }

        await routeAfterAuth();
        return;
      }

      clearSessionLogoutFlags();
    })();

    return () => subscription.unsubscribe();
  }, [navigate, pathname, routeAfterAuth]);

  /* ─────────────── Login with password ─────────────── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const { error: signInErr } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signInErr) {
      const lockedAccount = isLockedAccountError(signInErr);

      setError(
        lockedAccount
          ? "Account locked. Please contact your Compass administrator."
          : "User not found or incorrect password"
      );
      setLoading(false);
      await logAudit({
        category: "auth",
        action: "login_failed",
        success: false,
        detail: {
          method: "password",
          reason: lockedAccount ? "account_locked" : "invalid_credentials",
        },
      });
      return;
    }

    // Establish a fresh activity baseline on successful login.
    writeLastActivity(Date.now());

    await logAudit({
      category: "auth",
      action: "login_success",
      success: true,
      detail: { method: "password" },
    });

    // check MFA
    const { data: factors, error: factorsError } =
      await supabase.auth.mfa.listFactors();

    if (factorsError) {
      setError("Error checking MFA status: " + factorsError.message);
      setLoading(false);
      await logAudit({
        category: "mfa",
        action: "mfa_list_factors_failed",
        success: false,
        detail: { method: "totp", reason: factorsError.message },
      });
      return;
    }

    const totp = factors?.totp?.[0];

    if (totp && totp.status === "verified") {
      const { data: challenge, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId: totp.id });

      if (challengeError) {
        setError("Error creating MFA challenge: " + challengeError.message);
        setLoading(false);
        await logAudit({
          category: "mfa",
          action: "mfa_challenge_failed",
          success: false,
          detail: { method: "totp", reason: challengeError.message },
        });
        return;
      }

      setMfaFactorId(totp.id);
      setChallengeId(challenge?.id ?? challenge?.challenge?.id ?? null);
      setShow2FA(true);

      // stop spinner, show 2FA input (no routing yet)
      setLoading(false);
      return;
    }

    // No MFA required, route immediately, keep spinner/routing up
    setLoading(false);
    await routeAfterAuth();
  };

  /* ─────────────── Verify TOTP ─────────────── */
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const code = otpCode.trim();
    if (code.length !== 6) {
      setError("Please enter the 6-digit code.");
      setLoading(false);
      return;
    }

    const { error: verifyErr } = await supabase.auth.mfa.verify({
      factorId: mfaFactorId,
      challengeId,
      code,
    });

    if (verifyErr) {
      setError("Invalid code. Please try again.");
      setLoading(false);
      await logAudit({
        category: "mfa",
        action: "mfa_challenge_failed",
        success: false,
        detail: { method: "totp", reason: "invalid_code" },
      });
      return;
    }

    // After successful MFA, refresh baseline.
    writeLastActivity(Date.now());

    await logAudit({
      category: "mfa",
      action: "mfa_challenge_success",
      success: true,
      detail: { method: "totp" },
    });

    setLoading(false);
    await routeAfterAuth();
  };

  /* ─────────────── Forgot password ─────────────── */
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Please enter your email first.");
      return;
    }

    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (resetErr) {
      setError("Error sending reset email: " + resetErr.message);
      await logAudit({
        category: "auth",
        action: "password_reset_failed",
        success: false,
        detail: { email, error: resetErr.message },
      });
      return;
    }

    await alert("Password reset email sent. Please check your inbox.", {
      title: "Reset email sent",
      okLabel: "OK",
    });

    await logAudit({
      category: "auth",
      action: "password_reset_sent",
      success: true,
      detail: { email },
    });
  };

  const inputCls =
    "w-full px-4 py-2 rounded-md border border-app input-app focus:outline-none focus-visible:ring-app";
  const linkCls = "linkCls";
  const isOtpReady = otpCode.length === 6;

  // IMPORTANT: do not render Start form while loading or routing
  if (loading) return <LoadingUserScreen label="Signing in..." />;
  if (routing) return <LoadingUserScreen label="Redirecting..." />;

  return (
    <div className="min-h-screen bg-app text-app flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6 bg-surface">
        <div className="w-full max-w-md">
          <SectionCard>
            <div className="flex items-center justify-center mb-6 gap-1">
              <CompassLogo
                className="h-[40px] w-[40px] shrink-0 select-none text-brand dark:text-brand1-foreground"
                strokeWidth={2}
              />
              <h2 className="text-2xl font-semibold text-center text-brand dark:text-brand1-foreground">
                CoreCompass™
              </h2>
            </div>

            <form
              noValidate
              onSubmit={show2FA ? handleVerifyOTP : handleLogin}
              onKeyDown={handleFormKeyDown}
              className="space-y-4"
            >
              <div>
                <label className="block mb-1 font-medium">Email</label>
                <input
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                  required
                  aria-invalid={!!error}
                />
              </div>

              {!show2FA && (
                <div>
                  <label className="block mb-1 font-medium">Password</label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputCls}
                    required
                    aria-invalid={!!error}
                  />
                  <p className="mt-2 text-sm">
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className={linkCls}
                    >
                      Forgot password?
                    </button>
                  </p>
                </div>
              )}

              {show2FA && (
                <div>
                  <label className="block mb-1 font-medium">
                    Enter 6-digit code
                  </label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    className={inputCls}
                    required
                    aria-invalid={!!error}
                  />
                  <p className="mt-1 text-sm text-muted">
                    Check your authenticator app for a 6-digit code.
                  </p>
                </div>
              )}

              <button
                type="submit"
                ref={submitBtnRef}
                className="btn w-full btn-primary"
                disabled={show2FA ? !isOtpReady : false}
              >
                {show2FA ? "Verify Code" : "Login"}
              </button>
            </form>

            {error && (
              <div
                className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-center text-sm text-red-700"
                role="alert"
              >
                {error}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
      <FooterCompact />
    </div>
  );
}
